import { lazy } from "react";
import { Route } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Tournament management pages
const TournamentManagerDashboard = lazy(() => import("@/pages/tournament-manager/TournamentManagerDashboard"));

// Match management pages (formerly referee pages — accessible to club_staff and tournament_organizer)
const TournamentMatches = lazy(() => import("@/pages/referee/TournamentMatches"));
const MatchDetail = lazy(() => import("@/pages/referee/MatchDetail"));

// Public pages (no auth required)
const TVKioskPage = lazy(() => import("@/pages/live-tv/TVKioskPage"));
const PublicTournamentPage = lazy(() => import("@/pages/public/PublicTournamentPage"));
const PublicChampionshipPage = lazy(() => import("@/pages/public/PublicChampionshipPage"));
const RegiePage = lazy(() => import("@/pages/public/RegiePage"));
const TournamentManagerTournaments = lazy(() => import("@/pages/tournament-manager/TournamentManagerTournaments"));
const TournamentManagerValidations = lazy(() => import("@/pages/tournament-manager/TournamentManagerValidations"));
const TournamentManagerFFTTools = lazy(() => import("@/pages/tournament-manager/TournamentManagerFFTTools"));
const TournamentManagerNotifications = lazy(() => import("@/pages/tournament-manager/TournamentManagerNotifications"));
const CreateTournamentPage = lazy(() => import("@/pages/tournaments/CreateTournamentPage"));
const CreateChampionshipPage = lazy(() => import("@/pages/tournaments/CreateChampionshipPage"));
const CreateCompetitionPage = lazy(() => import("@/pages/tournaments/CreateCompetitionPage"));
const ManageTournament = lazy(() => import("@/pages/tournaments/ManageTournament"));
const ScoreEntryPage = lazy(() => import("@/components/matches/score/ScoreEntryPage"));
const ManageChampionship = lazy(() => import("@/pages/tournaments/ManageChampionship"));

/**
 * Tournament organizer routes
 * Routes for tournament_organizer role
 */
export const tournamentRoutes = (
  <>
    <Route
      path="/tournament-manager"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <TournamentManagerDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/tournaments"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <TournamentManagerTournaments />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/validations"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <TournamentManagerValidations />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/fft-tools"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <TournamentManagerFFTTools />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/notifications"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <TournamentManagerNotifications />
        </ProtectedRoute>
      }
    />
    {/* Nouvelle page de création unifiée - Route principale */}
    <Route
      path="/tournament-manager/create"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer"]}>
          <CreateCompetitionPage />
        </ProtectedRoute>
      }
    />
    {/* Routes legacy pour compatibilité - redirigent vers la nouvelle page */}
    <Route
      path="/tournament-manager/create-tournament"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer"]}>
          <CreateCompetitionPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/create-championship"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer"]}>
          <CreateCompetitionPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/tournaments/:id"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer"]}>
          <ManageTournament />
        </ProtectedRoute>
      }
    />
    <Route
      path="/tournament-manager/championships/:id"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer"]}>
          <ManageChampionship />
        </ProtectedRoute>
      }
    />
    {/*
      Saisie en série, au bord du terrain : même composant que la saisie joueur,
      second point de montage. `playerRoutes` est verrouillée à
      `requiredRoles={["player"]}` — un organisateur sans ce rôle y serait
      renvoyé sur /auth.
    */}
    <Route
      path="/tournament-manager/matches/:matchId/score"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <ScoreEntryPage />
        </ProtectedRoute>
      }
    />
    {/* Route alternative pour compatibilité avec liens existants et callbacks Stripe */}
    <Route
      path="/tournaments/:id/manage"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <ManageTournament />
        </ProtectedRoute>
      }
    />

    {/* Public tournament page - Shareable page with bracket, teams, results (no auth required) */}
    <Route
      path="/tournaments/:id"
      element={<PublicTournamentPage />}
    />
    {/* Public championship page — same template, no auth required */}
    <Route path="/championships/:id" element={<PublicChampionshipPage />} />

    {/* Régie par lien — publique : le jeton (dans le fragment #) fait office d'accès */}
    <Route path="/regie" element={<RegiePage />} />

    {/* Live TV Kiosk - Public display for tournaments (no auth required) */}
    <Route
      path="/live-tv/:tournamentId"
      element={<TVKioskPage />}
    />

    {/* Match management routes (formerly /referee/*) */}
    <Route
      path="/referee/tournaments/:tournamentId/matches"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <TournamentMatches />
        </ProtectedRoute>
      }
    />
    <Route
      path="/referee/matches/:matchId"
      element={
        <ProtectedRoute requiredRoles={["tournament_organizer", "club_staff", "association_staff"]}>
          <MatchDetail />
        </ProtectedRoute>
      }
    />
  </>
);
